import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.warn('Supabase env vars not set — using localStorage fallback.');
}

export const supabase = supabaseUrl && supabaseKey
  ? createClient(supabaseUrl, supabaseKey)
  : null;

// ── Poll helpers ──────────────────────────────────────────────────────────────

export async function createPoll(data) {
  // _rows is the full row definitions (labels, days, nth patterns).
  // Stored in its own 'rows_json' DB column so any participant on any
  // device can reconstruct time slot labels without localStorage.
  const _rows = data._rows || [];
  const dbData = Object.assign({}, data, { rows_json: _rows });
  delete dbData._rows;

  if (supabase) {
    const { data: row, error } = await supabase
      .from('polls')
      .insert(dbData)
      .select()
      .single();
    if (error) throw error;
    return Object.assign({}, row, { _rows: row.rows_json || [] });
  }

  // localStorage fallback
  const id  = crypto.randomUUID();
  const row = Object.assign({ id }, dbData, { created_at: new Date().toISOString() });
  localStorage.setItem('gm_poll_' + id, JSON.stringify(row));
  return Object.assign({}, row, { _rows });
}

export async function getPoll(id) {
  if (supabase) {
    const { data, error } = await supabase
      .from('polls')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    // Expose rows_json as _rows for the rest of the app
    return Object.assign({}, data, { _rows: data.rows_json || [] });
  }
  const raw = localStorage.getItem('gm_poll_' + id);
  if (!raw) throw new Error('Poll not found');
  const poll = JSON.parse(raw);
  return Object.assign({}, poll, { _rows: poll.rows_json || poll._rows || [] });
}

// ── Vote helpers ──────────────────────────────────────────────────────────────
// vote_type values: true (yes), false (no), 'ifneedbe'

export async function upsertVotes(pollId, participantName, voteMap) {
  const rows = Object.entries(voteMap).map(function(entry) {
    return {
      poll_id:          pollId,
      participant_name: participantName,
      rule_index:       Number(entry[0]),
      vote_type:        String(entry[1]),
    };
  });

  if (supabase) {
    const { error } = await supabase
      .from('votes')
      .upsert(rows, { onConflict: 'poll_id,participant_name,rule_index' });
    if (error) throw error;
    return;
  }

  const key      = 'gm_votes_' + pollId;
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  const filtered = existing.filter(function(v) { return v.participant_name !== participantName; });
  localStorage.setItem(key, JSON.stringify(filtered.concat(rows)));
}

export async function getVotes(pollId) {
  if (supabase) {
    const { data, error } = await supabase
      .from('votes')
      .select('*')
      .eq('poll_id', pollId);
    if (error) throw error;
    // Normalize vote_type strings back to JS values
    return data.map(function(v) {
      var vt = v.vote_type;
      if (vt === 'true')  vt = true;
      if (vt === 'false') vt = false;
      return Object.assign({}, v, { vote_type: vt });
    });
  }
  return JSON.parse(localStorage.getItem('gm_votes_' + pollId) || '[]');
}
